import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { suiteHubApi, docsApi, sheetsApi, slidesApi, calendarApi, whiteboardApi, studyPlanApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  FileText, FolderOpen, Calendar, MessageSquare, Mail, Video,
  Grid3X3, Presentation, PenTool, Search, ArrowLeft, Plus,
  Clock, ChevronRight, MailOpen, Users, ExternalLink, Flame, Target
} from 'lucide-react';

const SUITE_APPS = [
  { id: 'docs',       name: 'Docs',       icon: FileText,      path: '/docs',       color: '#4285F4', bg: 'rgba(66,133,244,0.08)' },
  { id: 'sheets',     name: 'Sheets',     icon: Grid3X3,       path: '/sheets',     color: '#0F9D58', bg: 'rgba(15,157,88,0.08)' },
  { id: 'slides',     name: 'Slides',     icon: Presentation,  path: '/slides',     color: '#F4B400', bg: 'rgba(244,180,0,0.08)' },
  { id: 'drive',      name: 'Drive',      icon: FolderOpen,    path: '/drive',      color: '#4285F4', bg: 'rgba(66,133,244,0.08)' },
  { id: 'calendar',   name: 'Calendar',   icon: Calendar,      path: '/calendar',   color: '#DB4437', bg: 'rgba(219,68,55,0.08)' },
  { id: 'chat',       name: 'Chat',       icon: MessageSquare, path: '/chat',       color: '#0F9D58', bg: 'rgba(15,157,88,0.08)' },
  { id: 'email',      name: 'Email',      icon: Mail,          path: '/email',      color: '#DB4437', bg: 'rgba(219,68,55,0.08)' },
  { id: 'meet',       name: 'Meet',       icon: Video,         path: '/meet',       color: '#00897B', bg: 'rgba(0,137,123,0.08)' },
  { id: 'whiteboard', name: 'Whiteboard', icon: PenTool,       path: '/whiteboard', color: '#7B1FA2', bg: 'rgba(123,31,162,0.08)' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Today ${time}`;
  if (isTomorrow) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

function WidgetCard({ title, icon: Icon, iconColor, viewAllPath, children, isEmpty, emptyText, emptyAction }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`widget-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {viewAllPath && (
          <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground" onClick={() => navigate(viewAllPath)}>
            View all <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        )}
      </div>
      <div className="p-3">
        {isEmpty ? (
          <div className="py-6 text-center">
            <p className="text-xs text-muted-foreground mb-2">{emptyText}</p>
            {emptyAction && (
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(emptyAction.path)}>
                <Plus className="w-3 h-3 mr-1" />{emptyAction.label}
              </Button>
            )}
          </div>
        ) : children}
      </div>
    </div>
  );
}

export default function SuiteHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [reminder, setReminder] = useState(null);

  useEffect(() => {
    suiteHubApi.getData()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { toast.error('Failed to load suite data'); setLoading(false); });
    studyPlanApi.streak().then(r => setStreakData(r)).catch(() => {});
    studyPlanApi.reminder().then(r => setReminder(r)).catch(() => {});
  }, []);

  const quickCreate = async (type) => {
    setCreating(type);
    try {
      if (type === 'doc') {
        const doc = await docsApi.create({ title: 'Untitled Document' });
        navigate(`/docs/${doc.doc_id}`);
      } else if (type === 'sheet') {
        const s = await sheetsApi.create({ title: 'Untitled Spreadsheet' });
        navigate(`/sheets/${s.sheet_id}`);
      } else if (type === 'slide') {
        const p = await slidesApi.create({ title: 'Untitled Presentation' });
        navigate(`/slides/${p.presentation_id}`);
      } else if (type === 'whiteboard') {
        const wb = await whiteboardApi.create({ title: 'Untitled Whiteboard' });
        navigate(`/whiteboard/${wb.whiteboard_id}`);
      } else if (type === 'event') {
        navigate('/calendar');
      }
    } catch (e) {
      toast.error(`Failed to create ${type}`);
    } finally {
      setCreating(null);
    }
  };

  const filteredApps = SUITE_APPS.filter(a => a.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-background" data-testid="suite-hub">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 via-green-400 to-yellow-400 flex items-center justify-center">
              <Grid3X3 className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-foreground tracking-tight">Suite</h1>
          </div>
          <div className="relative w-72 hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search apps..."
              className="pl-9 h-9 bg-muted/50"
              data-testid="suite-search"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Welcome + Quick Create */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground mb-1">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}
            </h2>
            <p className="text-sm text-muted-foreground">Your workspace at a glance</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="quick-create-bar">
            {[
              { type: 'doc', label: 'New Doc', icon: FileText, color: '#4285F4' },
              { type: 'sheet', label: 'New Sheet', icon: Grid3X3, color: '#0F9D58' },
              { type: 'slide', label: 'New Slides', icon: Presentation, color: '#F4B400' },
              { type: 'whiteboard', label: 'New Board', icon: PenTool, color: '#7B1FA2' },
              { type: 'event', label: 'New Event', icon: Calendar, color: '#DB4437' },
            ].map(({ type, label, icon: Icon, color }) => (
              <Button key={type} variant="outline" size="sm"
                className="h-8 text-xs gap-1.5 border-dashed"
                disabled={creating === type}
                onClick={() => quickCreate(type)}
                data-testid={`quick-create-${type}`}
              >
                <Plus className="w-3 h-3" />
                <Icon className="w-3.5 h-3.5" style={{ color }} />
                {creating === type ? 'Creating...' : label}
              </Button>
            ))}
          </div>
        </div>

        {/* Study Streak + Today's Plan card */}
        {(streakData || reminder?.has_reminder) && (
          <Link
            to="/study-plan"
            className="block rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-transparent p-4 hover:from-orange-500/15 transition-colors"
            data-testid="suite-streak-card"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
                  <Flame className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <div className="text-base font-bold text-foreground">
                    {streakData?.streak > 0 ? `${streakData.streak}-day streak` : 'Start your streak today'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {reminder?.has_reminder
                      ? `${reminder.remaining_min} min left today (${reminder.progress_pct}% done)`
                      : streakData?.last_active
                        ? `Last active ${streakData.last_active}`
                        : 'Complete one block today to begin'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 text-sm font-medium">
                <Target className="w-4 h-4" />
                Open Daily Plan
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        )}

        {/* App Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3" data-testid="app-grid">
          {filteredApps.map(app => (
            <Link key={app.id} to={app.path}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-transparent hover:border-border hover:shadow-sm transition-all group"
              data-testid={`app-tile-${app.id}`}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ backgroundColor: app.bg }}>
                <app.icon className="w-6 h-6" style={{ color: app.color }} />
              </div>
              <span className="text-xs font-medium text-foreground">{app.name}</span>
            </Link>
          ))}
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        )}

        {/* Widgets */}
        {data && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Recent Docs */}
            <WidgetCard title="Recent Documents" icon={FileText} iconColor="#4285F4" viewAllPath="/docs"
              isEmpty={!data.recent_docs?.length} emptyText="No documents yet" emptyAction={{ path: '/docs', label: 'Create Doc' }}>
              <div className="space-y-1">
                {data.recent_docs?.map(doc => (
                  <Link key={doc.doc_id} to={`/docs/${doc.doc_id}`}
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors group">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{doc.title || 'Untitled'}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(doc.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Upcoming Events */}
            <WidgetCard title="Upcoming Events" icon={Calendar} iconColor="#DB4437" viewAllPath="/calendar"
              isEmpty={!data.upcoming_events?.length} emptyText="No upcoming events" emptyAction={{ path: '/calendar', label: 'Add Event' }}>
              <div className="space-y-1">
                {data.upcoming_events?.map(evt => (
                  <Link key={evt.event_id} to="/calendar"
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: evt.color || '#DB4437' }} />
                      <span className="text-sm text-foreground truncate">{evt.title}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2 whitespace-nowrap">
                      {formatEventDate(evt.start)}
                    </span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Email */}
            <WidgetCard title={`Email${data.unread_email_count ? ` (${data.unread_email_count})` : ''}`} icon={Mail} iconColor="#DB4437" viewAllPath="/email"
              isEmpty={!data.recent_emails?.length} emptyText="Inbox is empty" emptyAction={{ path: '/email', label: 'Compose' }}>
              <div className="space-y-1">
                {data.recent_emails?.map(em => (
                  <Link key={em.email_id} to="/email"
                    className={`flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors ${!em.read ? 'bg-blue-500/5' : ''}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {!em.read ? <MailOpen className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${!em.read ? 'font-semibold text-foreground' : 'text-foreground'}`}>{em.subject || '(no subject)'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{em.from_name}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(em.sent_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Active Meetings */}
            <WidgetCard title="Meetings" icon={Video} iconColor="#00897B" viewAllPath="/meet"
              isEmpty={!data.active_meetings?.length} emptyText="No active meetings" emptyAction={{ path: '/meet', label: 'New Meeting' }}>
              <div className="space-y-1">
                {data.active_meetings?.map(room => (
                  <Link key={room.room_id} to={`/meet/${room.room_id}`}
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <Video className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{room.name}</p>
                        <p className="text-[10px] text-muted-foreground">{room.host_name}</p>
                      </div>
                    </div>
                    <span className="text-[10px] bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">Live</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Recent Sheets */}
            <WidgetCard title="Recent Sheets" icon={Grid3X3} iconColor="#0F9D58" viewAllPath="/sheets"
              isEmpty={!data.recent_sheets?.length} emptyText="No spreadsheets yet" emptyAction={{ path: '/sheets', label: 'Create Sheet' }}>
              <div className="space-y-1">
                {data.recent_sheets?.map(s => (
                  <Link key={s.sheet_id} to={`/sheets/${s.sheet_id}`}
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <Grid3X3 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{s.title || 'Untitled'}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(s.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Recent Slides */}
            <WidgetCard title="Recent Slides" icon={Presentation} iconColor="#F4B400" viewAllPath="/slides"
              isEmpty={!data.recent_slides?.length} emptyText="No presentations yet" emptyAction={{ path: '/slides', label: 'Create Slides' }}>
              <div className="space-y-1">
                {data.recent_slides?.map(s => (
                  <Link key={s.presentation_id} to={`/slides/${s.presentation_id}`}
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <Presentation className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{s.title || 'Untitled'}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(s.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Chat */}
            <WidgetCard title="Recent Chats" icon={MessageSquare} iconColor="#0F9D58" viewAllPath="/chat"
              isEmpty={!data.recent_chats?.length} emptyText="No conversations yet" emptyAction={{ path: '/chat', label: 'Start Chat' }}>
              <div className="space-y-1">
                {data.recent_chats?.map(c => (
                  <Link key={c.conversation_id} to="/chat"
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <Users className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{c.name || 'Chat'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{c.last_message?.text || 'No messages'}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(c.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Drive */}
            <WidgetCard title="Drive Files" icon={FolderOpen} iconColor="#4285F4" viewAllPath="/drive"
              isEmpty={!data.recent_drive_files?.length} emptyText="Drive is empty" emptyAction={{ path: '/drive', label: 'Go to Drive' }}>
              <div className="space-y-1">
                {data.recent_drive_files?.map(f => (
                  <Link key={f.file_id} to="/drive"
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{f.name}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(f.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>

            {/* Whiteboard */}
            <WidgetCard title="Whiteboards" icon={PenTool} iconColor="#7B1FA2" viewAllPath="/whiteboard"
              isEmpty={!data.recent_whiteboards?.length} emptyText="No whiteboards yet" emptyAction={{ path: '/whiteboard', label: 'Create Board' }}>
              <div className="space-y-1">
                {data.recent_whiteboards?.map(wb => (
                  <Link key={wb.whiteboard_id} to={`/whiteboard/${wb.whiteboard_id}`}
                    className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <PenTool className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{wb.title || 'Untitled'}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{timeAgo(wb.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </WidgetCard>
          </div>
        )}
      </main>
    </div>
  );
}
